// src/pages/HtmlPage.js
import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
import PillButton from '../components/PillButton';
import SEO from '../components/SEO';
import CodePlayground from '../components/CodePlayground';
import Quiz from '../components/Quiz';
import CopyButton from '../components/CopyButton';

function HtmlPage() {


  return (
    <div className="container mt-5">
      <SEO 
        title="HTML Tutorials | The Code-E Project"
        description="Learn HTML fundamentals including tags, attributes, document structure, semantic elements, forms, and best practices for modern web development."
        keywords="HTML, HTML5, semantic HTML, HTML tags, HTML attributes, web development, frontend, HTML tutorial"
      />
      
      {/* Hero Section */}
      <header className="hero-section text-center" role="banner">
        <div className="container">
          <h1>HTML</h1>
          <p className="lead">Structure your web content</p>
        </div>
      </header>

      {/* Main Content */}
      <main className="container py-5" role="main">
        <section id="html-content" aria-labelledby="html-heading">
          <h2 id="html-heading">Learn HTML Basics</h2>
          <p>
            HTML (HyperText Markup Language) is the foundation of every webpage. Below, explore key concepts in interactive chunks!
          </p>

          {/* Chunk 1: Basic Structure */}
          <div className="chunk">
            <h3>1. Basic HTML Structure</h3>
            <p>Every HTML document starts with a standard structure.</p>
            <div className="code-snippet">
              <pre>
                {`<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <title>My Page</title>
</head>
<body>
  <h1>Hello World</h1>
</body>
</html>`}
              </pre>
              <CopyButton text={`<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <title>My Page</title>
</head>
<body>
  <h1>Hello World</h1>
</body>
</html>`} />
            </div>
            <div className="tip">
              <strong>VS Code Tip:</strong> Type <code>!</code> and press <strong>Tab</strong> to auto-generate this structure!
            </div>
          </div>

          {/* Chunk 2: Headings */}
          <div className="chunk">
            <h3>2. Headings & Playground</h3>
            <p>Use <code>&lt;h1&gt;</code> to <code>&lt;h6&gt;</code>. Try changing the text below!</p>
            <CodePlayground initialCode={`<h1>Main Title</h1>
<h2>Subheading</h2>
<h3>Smaller Heading</h3>
<p>Change me and see the result!</p>`} />
          </div>

          {/* Chunk 3: Paragraphs and Text */}
          <div className="chunk">
            <h3>3. Paragraphs and Text</h3>
            <p>Use <code>&lt;p&gt;</code> for paragraphs and <code>&lt;b&gt;</code>, <code>&lt;i&gt;</code> for formatting.</p>
            <div className="code-snippet">
              <pre>
                {`<p>This is a paragraph.</p>
<p><b>Bold</b> and <i>italic</i> text.</p>`}
              </pre>
            </div>
            <div className="tip">
              <strong>VS Code Tip:</strong> Use <strong>Cmd + /</strong> (Mac) or <strong>Ctrl + /</strong> (Windows) to toggle comments on selected lines.
            </div>
          </div>

          {/* Chunk 4: Links */}
          <div className="chunk">
            <h3>4. Links</h3>
            <p>Create hyperlinks with the <code>&lt;a&gt;</code> tag.</p>
            <div className="code-snippet">
              <pre>
                {`<a href="https://codie.codes">Visit Code-E</a>`}
              </pre>
            </div>
            <p>
              <strong>Try it:</strong>{' '}
              <PillButton onClick={() => alert('You clicked a link!')}>
                Test Link
              </PillButton>
            </p>
            <div className="tip">
              <strong>VS Code Tip:</strong> Hold <strong>Option</strong> (Mac) or <strong>Alt</strong> (Windows) and click to place multiple cursors.
            </div>
          </div>

          {/* Chunk 5: Images */}
          <div className="chunk">
            <h3>5. Images</h3>
            <p>Add images with <code>&lt;img&gt;</code> (self-closing tag).</p>
            <div className="code-snippet">
              <pre>
                {`<img src="image.jpg" alt="Description" width="200">`}
              </pre>
            </div>
            <div className="tip">
              <strong>VS Code Tip:</strong> Press <strong>Cmd + T</strong> (Mac) or <strong>Ctrl + T</strong> (Windows) to quickly search for symbols or files.
            </div>
          </div>

          {/* Chunk 6: Lists */}
          <div className="chunk">
            <h3>6. Lists</h3>
            <p>Use <code>&lt;ul&gt;</code> for unordered and <code>&lt;ol&gt;</code> for ordered lists.</p>
            <div className="code-snippet">
              <pre>
                {`<ul>
  <li>Item 1</li>
  <li>Item 2</li>
</ul>
<ol>
  <li>First</li>
  <li>Second</li>
</ol>`}
              </pre>
            </div>
            <div className="tip">
              <strong>VS Code Tip:</strong> Use <strong>Cmd + Shift + L</strong> (Mac) or <strong>Ctrl + Shift + L</strong> (Windows) to select all occurrences of a word.
            </div>
          </div>

          {/* Link to Semantic HTML Page */}
          
          {/* Quiz Section */}
          <div className="mt-5">
            <h3>Test Your Knowledge</h3>
            <Quiz 
              question="Which tag is used for the largest heading in HTML?"
              options={["<head>", "<h6>", "<h1>", "<heading>"]}
              correctAnswerIndex={2}
            />
          </div>

          <div className="text-center mt-4">
            <NavLink to="/semantic-html">
              <PillButton>Explore Semantic HTML Guide</PillButton>
            </NavLink>
          </div>
        </section>
      </main>
    </div>
  );
}

export default HtmlPage;